import type {
  TallyExportPreview,
  TallyPreviewRow,
  TallySkippedReceipt,
} from "@/utils/tally-export-preview";

import { AssignVoucherNumberError } from "./assign-voucher-numbers";
import { buildBankTallyVouchers, type BankTallyMapping } from "./build-bank-tally-vouchers";
import {
  BANK_SKIP_REASON_DEBIT,
  type BankCreditTxn,
  type BankSkippedRow,
} from "./bank-statement-types";

export type BankTallySkippedReasonCount = {
  reason: string;
  count: number;
};

export type BankTallyPreviewSummary = {
  creditCount: number;
  creditTotal: number;
  skippedCount: number;
  debitSkippedCount: number;
  skippedByReason: BankTallySkippedReasonCount[];
};

/** Aggregate credits + skipped rows for the preview header/alerts. */
export function buildBankTallyPreviewSummary(
  credits: BankCreditTxn[],
  skipped: BankSkippedRow[]
): BankTallyPreviewSummary {
  const creditTotal = credits.reduce((sum, c) => sum + c.amount, 0);
  const reasonCounts = new Map<string, number>();
  let debitSkippedCount = 0;

  for (const row of skipped) {
    reasonCounts.set(row.reason, (reasonCounts.get(row.reason) ?? 0) + 1);
    if (row.reason === BANK_SKIP_REASON_DEBIT) {
      debitSkippedCount += 1;
    }
  }

  const skippedByReason = [...reasonCounts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));

  return {
    creditCount: credits.length,
    creditTotal,
    skippedCount: skipped.length,
    debitSkippedCount,
    skippedByReason,
  };
}

export function bankSkippedToTallySkipped(skipped: BankSkippedRow[]): TallySkippedReceipt[] {
  return skipped.map((row) => {
    const reasons = [row.reason];
    if (row.description?.trim()) {
      reasons.push(row.description.trim());
    }
    if (row.amount != null && row.amount > 0) {
      reasons.push(`Amount: ${row.amount}`);
    }
    if (row.txnDateRaw?.trim()) {
      reasons.push(`Txn date: ${row.txnDateRaw.trim()}`);
    }
    return {
      receiptId: `bank-row-${row.rowNumber}`,
      receiptNo: row.txnNo?.trim() || `Row ${row.rowNumber}`,
      reasons,
    };
  });
}

export type BuildBankTallyExportPreviewInput = {
  credits: BankCreditTxn[];
  skipped: BankSkippedRow[];
  mapping: BankTallyMapping;
  prefix: string;
  seed: string;
};

/**
 * Builds the same `TallyExportPreview` shape used by receipt Tally export
 * (Cr/Dr voucher lines + skipped receipts for the shared preview dialog).
 */
export function buildBankTallyExportPreview(
  input: BuildBankTallyExportPreviewInput
): TallyExportPreview {
  const voucherRows = buildBankTallyVouchers({
    credits: input.credits,
    mapping: input.mapping,
    prefix: input.prefix,
    seed: input.seed,
  });

  const rows: TallyPreviewRow[] = voucherRows.map((row, index) => ({
    ...row,
    id: `bank-${row.voucherNumber || "line"}-${index}`,
    rowIssues: [],
  }));

  return {
    rows,
    skipped: bankSkippedToTallySkipped(input.skipped),
  };
}

export type TryBuildBankTallyExportPreviewResult =
  | { ok: true; preview: TallyExportPreview }
  | { ok: false; message: string };

export function tryBuildBankTallyExportPreview(
  input: BuildBankTallyExportPreviewInput
): TryBuildBankTallyExportPreviewResult {
  try {
    return { ok: true, preview: buildBankTallyExportPreview(input) };
  } catch (err) {
    const message =
      err instanceof AssignVoucherNumberError
        ? err.message
        : err instanceof Error
          ? err.message
          : "Could not build Tally preview.";
    return { ok: false, message };
  }
}
